/**
 * Social Security claiming optimizer (spec §8): grid-search every combination
 * of claiming ages 62-70 for both people, scoring each on
 *   - present-valued lifetime benefits (user-set discount rate), and
 *   - the effect on the FULL plan (ending net worth, depletion) — because
 *     claiming interacts with the drawdown, taxes, and the conversion window.
 *
 * The whole surface is returned, not just the argmax — the shape tells the
 * user how much the decision actually matters.
 */
import type { Plan } from '../model/types';
import { runProjection } from './projection';
import { CURRENT_YEAR } from '../model/defaults';

export const CLAIMING_AGES = [62, 63, 64, 65, 66, 67, 68, 69, 70];

export interface ClaimingCell {
  /** Claiming age per person id for this cell. */
  agesByPerson: Record<string, number>;
  endingNetWorth: number;
  /** PV of gross SS benefits over the plan, discounted to the start year. */
  pvBenefits: number;
  depletionYear?: number;
}

export interface ClaimingGridResult {
  personIds: string[];
  ages: number[];
  /** cells[i][j]: person0 claims ages[i]; person1 (if any) claims ages[j].
   *  For a single person the grid is 1 x ages.length. */
  cells: ClaimingCell[][];
  best: ClaimingCell;
  current: ClaimingCell;
}

export function claimingGrid(plan: Plan, discountRate = 0.03): ClaimingGridResult {
  const people = plan.household.people.filter(
    (p) => (p.socialSecurity.estimatedMonthlyAtFRA ?? 0) > 0 ||
      (p.socialSecurity.earningsHistory?.length ?? 0) > 0,
  );
  const personIds = people.map((p) => p.id);

  const evalCombo = (agesByPerson: Record<string, number>): ClaimingCell => {
    const variant: Plan = structuredClone(plan);
    variant.decisions.claimingAgeOverride = agesByPerson;
    const r = runProjection(variant, CURRENT_YEAR);
    const pvBenefits = r.snapshots.reduce(
      (t, s) => t + s.socialSecurityGross / Math.pow(1 + discountRate, s.year - CURRENT_YEAR),
      0,
    );
    const last = r.snapshots[r.snapshots.length - 1];
    return {
      agesByPerson,
      endingNetWorth: last?.netWorth ?? 0,
      pvBenefits,
      depletionYear: r.firstDepletionYear,
    };
  };

  const cells: ClaimingCell[][] = [];
  if (personIds.length <= 1) {
    const pid = personIds[0];
    cells.push(
      CLAIMING_AGES.map((age) => evalCombo(pid ? { [pid]: age } : {})),
    );
  } else {
    for (const a0 of CLAIMING_AGES) {
      const row: ClaimingCell[] = [];
      for (const a1 of CLAIMING_AGES) {
        row.push(evalCombo({ [personIds[0]!]: a0, [personIds[1]!]: a1 }));
      }
      cells.push(row);
    }
  }

  const flat = cells.flat();
  const best = flat.reduce((b, c) => (c.endingNetWorth > b.endingNetWorth ? c : b), flat[0]!);

  const currentAges: Record<string, number> = {};
  for (const p of people) {
    currentAges[p.id] =
      plan.decisions.claimingAgeOverride?.[p.id] ?? p.socialSecurity.claimingAge;
  }
  const current = evalCombo(currentAges);

  return { personIds, ages: CLAIMING_AGES, cells, best, current };
}
